/*
 * Página de Mensajes internos.
 *
 * Vista con 3 pestañas: Recibidos · Enviados · Nuevo.
 * Al hacer click en un mensaje, abre el hilo (todos los mensajes del thread)
 * y permite responder inline.
 *
 * Reference opcional: al redactar un mensaje se puede adjuntar un "proyecto"
 * (site, incidencia, negociación, sar, preventivo). El bloque de referencia
 * aparece en el hilo como una tarjeta clicable que abre el recurso.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Inbox, Send, PenSquare, Loader2, Trash2, Reply, ExternalLink, X, Mail, MailOpen, Paperclip, ArrowLeft } from 'lucide-react';

// -------------------- helpers --------------------
const REF_META = {
  site:         { label: 'Emplazamiento', color: 'text-[#A3FF12] bg-[#A3FF12]/10 border-[#A3FF12]/30', path: '/app/emplazamientos' },
  incident:     { label: 'Incidencia',    color: 'text-red-300 bg-red-500/10 border-red-500/30',        path: '/app/incidencias' },
  negotiation:  { label: 'Negociación',   color: 'text-blue-300 bg-blue-500/10 border-blue-500/30',     path: '/app/bajada-renta' },
  sar:          { label: 'SAR',           color: 'text-purple-300 bg-purple-500/10 border-purple-500/30', path: '/app/prospecciones' },
  preventive:   { label: 'Preventivo',    color: 'text-orange-300 bg-orange-500/10 border-orange-500/30', path: '/app/preventivo' },
  project:      { label: 'Proyecto',      color: 'text-white/80 bg-white/10 border-white/20',           path: '/app' },
};

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const min = Math.round(diffMs / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.round(h / 24);
  if (days < 7) return `hace ${days} d`;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
};

// -------------------- New / Reply composer --------------------
const Composer = ({ recipients, defaultTo, defaultSubject, threadId, onSent, onCancel }) => {
  const [toId, setToId] = useState(defaultTo || '');
  const [subject, setSubject] = useState(defaultSubject || '');
  const [body, setBody] = useState('');
  const [refType, setRefType] = useState('');
  const [refId, setRefId] = useState('');
  const [refLabel, setRefLabel] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');

  const send = async () => {
    setErr('');
    if (!toId || !subject.trim() || !body.trim()) {
      setErr('Elige destinatario, asunto y escribe un mensaje');
      return;
    }
    setSending(true);
    try {
      const payload = { to_user_id: toId, subject: subject.trim(), body: body.trim() };
      if (threadId) payload.thread_id = threadId;
      if (refType && refId && refLabel) {
        const meta = REF_META[refType];
        payload.reference = { type: refType, id: refId.trim(), label: refLabel.trim(), url: `${meta?.path || '/app'}?focus=${encodeURIComponent(refId.trim())}` };
      }
      await api.post('/messages', payload);
      onSent();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Error al enviar');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 space-y-3" data-testid="msg-composer">
      {!threadId && (
        <div>
          <label className="text-white/70 text-xs font-bold uppercase tracking-wider">Para</label>
          <select value={toId} onChange={(e) => setToId(e.target.value)}
            data-testid="msg-to"
            className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#A3FF12]">
            <option value="">— Elige destinatario —</option>
            {recipients.map((r) => (
              <option key={r.id} value={r.id}>{r.name} · {r.email} ({r.role})</option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="text-white/70 text-xs font-bold uppercase tracking-wider">Asunto</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)}
          data-testid="msg-subject"
          disabled={!!threadId}
          placeholder="Asunto del mensaje"
          className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#A3FF12] disabled:opacity-60"/>
      </div>
      <div>
        <label className="text-white/70 text-xs font-bold uppercase tracking-wider">Mensaje</label>
        <textarea value={body} onChange={(e) => setBody(e.target.value)}
          data-testid="msg-body"
          rows={6}
          placeholder="Escribe aquí…"
          className="w-full mt-1 bg-black border border-white/15 text-white rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#A3FF12] resize-none"/>
      </div>
      {!threadId && (
        <details className="bg-white/5 border border-white/10 rounded-xl">
          <summary className="cursor-pointer px-3 py-2 text-white/70 text-xs font-bold flex items-center gap-2">
            <Paperclip size={12}/> Adjuntar referencia a proyecto (opcional)
          </summary>
          <div className="p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={refType} onChange={(e) => setRefType(e.target.value)}
              data-testid="msg-ref-type"
              className="bg-black border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-[#A3FF12]/50">
              <option value="">— Tipo —</option>
              {Object.entries(REF_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <input value={refId} onChange={(e) => setRefId(e.target.value)}
              data-testid="msg-ref-id"
              placeholder="ID / código"
              className="bg-black border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
            <input value={refLabel} onChange={(e) => setRefLabel(e.target.value)}
              data-testid="msg-ref-label"
              placeholder="Etiqueta (ej. GR6133)"
              className="bg-black border border-white/10 rounded-lg px-2 py-1.5 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
          </div>
        </details>
      )}
      {err && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-3 py-2">{err}</div>}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <button onClick={onCancel} className="text-white/60 hover:text-white text-xs font-bold px-3 py-2">
            Cancelar
          </button>
        )}
        <button onClick={send} disabled={sending}
          data-testid="msg-send"
          className="bg-[#A3FF12] text-black font-black px-5 py-2 rounded-xl hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center gap-2 text-sm">
          {sending ? <Loader2 className="animate-spin" size={14}/> : <Send size={14}/>}
          {threadId ? 'Responder' : 'Enviar'}
        </button>
      </div>
    </div>
  );
};

// -------------------- Thread view --------------------
const ThreadView = ({ threadId, currentUser, onBack, onChanged }) => {
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replying, setReplying] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/messages/thread/${threadId}`);
      setMsgs(r.data || []);
      onChanged();
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [threadId, onChanged]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-white/50">
        <Loader2 className="animate-spin mr-2" size={16}/> Cargando…
      </div>
    );
  }
  if (msgs.length === 0) {
    return <div className="text-white/50 text-center py-10">Hilo vacío</div>;
  }

  const first = msgs[0];
  const other = first.from_user_id === currentUser.id
    ? { id: first.to_user_id, name: first.to_user_name, email: first.to_user_email }
    : { id: first.from_user_id, name: first.from_user_name, email: first.from_user_email };

  const deleteThread = async () => {
    if (!window.confirm('¿Eliminar todo este hilo de tu bandeja?')) return;
    await api.delete(`/messages/thread/${threadId}`);
    onBack(); onChanged();
  };

  return (
    <div className="space-y-3" data-testid="msg-thread-view">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-white/60 hover:text-white flex items-center gap-1.5 text-sm font-bold" data-testid="msg-back">
          <ArrowLeft size={14}/> Volver
        </button>
        <button onClick={deleteThread} className="text-red-300/70 hover:text-red-300 flex items-center gap-1 text-xs font-bold" data-testid="msg-delete-thread">
          <Trash2 size={12}/> Eliminar hilo
        </button>
      </div>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-widest text-[#A3FF12] font-black mb-1">HILO</div>
        <div className="text-white font-black text-lg">{first.subject}</div>
        <div className="text-white/50 text-xs mt-0.5">Con <b className="text-white/80">{other.name}</b> ({other.email})</div>
      </div>

      <div className="space-y-2">
        {msgs.map((m) => {
          const mine = m.from_user_id === currentUser.id;
          return (
            <div key={m.id} data-testid={`msg-item-${m.id}`}
              className={`bg-[#0a0a0a] border rounded-2xl p-4 ${mine ? 'border-[#A3FF12]/20 ml-6' : 'border-white/10 mr-6'}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-bold">
                  {mine ? <span className="text-[#A3FF12]">Tú</span> : <span className="text-white">{m.from_user_name}</span>}
                </div>
                <div className="text-white/40 text-[10px]">{new Date(m.created_at).toLocaleString('es-ES')}</div>
              </div>
              <div className="text-white/85 text-sm whitespace-pre-wrap leading-relaxed">{m.body}</div>
              {m.reference && (
                <a href={m.reference.url || '#'} target="_blank" rel="noreferrer"
                  className={`mt-3 inline-flex items-center gap-2 border rounded-lg px-3 py-1.5 text-xs font-bold ${REF_META[m.reference.type]?.color || 'text-white/60 bg-white/5 border-white/10'}`}>
                  {REF_META[m.reference.type]?.label || 'Referencia'} · {m.reference.label}
                  <ExternalLink size={11}/>
                </a>
              )}
            </div>
          );
        })}
      </div>

      {!replying ? (
        <button onClick={() => setReplying(true)}
          data-testid="msg-reply-open"
          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl py-3 text-white/80 font-bold text-sm flex items-center justify-center gap-2">
          <Reply size={14}/> Responder
        </button>
      ) : (
        <Composer
          recipients={[]}
          defaultTo={other.id}
          defaultSubject={first.subject.startsWith('Re:') ? first.subject : `Re: ${first.subject}`}
          threadId={threadId}
          onCancel={() => setReplying(false)}
          onSent={() => { setReplying(false); load(); }}
        />
      )}
    </div>
  );
};

// -------------------- Main page --------------------
const Mensajes = () => {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab') || 'inbox');
  const [inboxItems, setInboxItems] = useState([]);
  const [sentItems, setSentItems] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [openThreadId, setOpenThreadId] = useState(params.get('thread') || null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [i, s, r] = await Promise.all([
        api.get('/messages/inbox'),
        api.get('/messages/sent'),
        api.get('/messages/recipients'),
      ]);
      setInboxItems(i.data || []);
      setSentItems(s.data || []);
      setRecipients(r.data || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    // sync url params
    const p = new URLSearchParams();
    if (tab && tab !== 'inbox') p.set('tab', tab);
    if (openThreadId) p.set('thread', openThreadId);
    setParams(p, { replace: true });
  }, [tab, openThreadId, setParams]);

  const openThread = (threadId) => {
    setOpenThreadId(threadId);
  };
  const closeThread = () => setOpenThreadId(null);

  const list = tab === 'inbox' ? inboxItems : sentItems;
  const unread = inboxItems.filter((m) => !m.read_at).length;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 lg:py-10 max-w-5xl">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-[#A3FF12] mb-2">COMUNICACIÓN</div>
        <h1 className="text-3xl lg:text-4xl font-black text-white">Mensajes</h1>
        <p className="text-white/50 mt-2 text-sm">Envía mensajes o comparte proyectos (sites, incidencias, negociaciones, SAR…) con tu equipo.</p>
      </div>

      {openThreadId ? (
        <ThreadView
          threadId={openThreadId}
          currentUser={user}
          onBack={closeThread}
          onChanged={reload}
        />
      ) : (
        <>
          {/* Tabs */}
          <div className="flex items-center gap-2 mb-4 border-b border-white/10">
            {[
              { id: 'inbox', label: 'Recibidos', icon: Inbox, count: unread },
              { id: 'sent',  label: 'Enviados',  icon: Send },
              { id: 'new',   label: 'Nuevo',     icon: PenSquare },
            ].map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`msg-tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2.5 border-b-2 text-sm font-bold transition-colors ${
                  tab === t.id ? 'border-[#A3FF12] text-[#A3FF12]' : 'border-transparent text-white/50 hover:text-white/80'
                }`}>
                <t.icon size={14}/> {t.label}
                {t.count > 0 && (
                  <span className="ml-1 bg-[#A3FF12] text-black text-[10px] font-black rounded-full px-1.5 py-0.5">
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {tab === 'new' ? (
            <Composer
              recipients={recipients}
              onSent={() => { setTab('sent'); reload(); }}
              onCancel={() => setTab('inbox')}
            />
          ) : (
            <div>
              {loading ? (
                <div className="text-white/50 flex items-center gap-2 py-8 justify-center">
                  <Loader2 className="animate-spin" size={16}/> Cargando…
                </div>
              ) : list.length === 0 ? (
                <div className="text-white/40 text-center py-16 bg-[#0a0a0a] border border-dashed border-white/10 rounded-2xl">
                  {tab === 'inbox' ? 'No tienes mensajes recibidos' : 'No has enviado mensajes'}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {list.map((m) => {
                    const isUnread = tab === 'inbox' && !m.read_at;
                    const other = tab === 'inbox'
                      ? { name: m.from_user_name, email: m.from_user_email }
                      : { name: m.to_user_name, email: m.to_user_email };
                    const ref = m.reference;
                    const meta = ref ? REF_META[ref.type] : null;
                    return (
                      <button key={m.id} onClick={() => openThread(m.thread_id)}
                        data-testid={`msg-list-item-${m.id}`}
                        className={`w-full text-left bg-[#0a0a0a] hover:bg-white/[0.04] border rounded-2xl px-4 py-3.5 flex items-center gap-3 transition-colors ${
                          isUnread ? 'border-[#A3FF12]/40' : 'border-white/10'
                        }`}>
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isUnread ? 'bg-[#A3FF12]/20 text-[#A3FF12]' : 'bg-white/5 text-white/40'
                        }`}>
                          {isUnread ? <Mail size={16}/> : <MailOpen size={16}/>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`truncate font-bold text-sm ${isUnread ? 'text-white' : 'text-white/80'}`}>
                              {other.name}
                            </span>
                            <span className="text-white/40 text-xs">·</span>
                            <span className="text-white/40 text-xs">{relTime(m.created_at)}</span>
                          </div>
                          <div className={`truncate text-sm ${isUnread ? 'text-white font-bold' : 'text-white/70'}`}>
                            {m.subject}
                          </div>
                          <div className="text-white/40 text-xs truncate mt-0.5">{m.body}</div>
                          {ref && (
                            <span className={`inline-flex items-center gap-1 mt-1.5 border rounded px-1.5 py-0.5 text-[10px] font-bold ${meta?.color || 'text-white/60 border-white/10'}`}>
                              <Paperclip size={9}/> {meta?.label || 'Ref.'} · {ref.label}
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Mensajes;
